import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Resizable } from "re-resizable";
import { ChevronDown, Plus, Sparkles, X } from "lucide-react";

import BetaBadge from "./BetaBadge";
import ChatComposer from "./ChatComposer";
import ChatContextBar from "./ChatContextBar";
import ChatEmptyState from "./ChatEmptyState";
import ChatThread from "./ChatThread";
import PromptGallery from "./PromptGallery";
import PromptPickerPopover from "./PromptPickerPopover";
import { CHAT_PROMPT_CUSTOM_TYPE } from "../constants/chatConstants";
import { useChatAgentLoopFlag } from "../hooks/useChatAgentLoopFlag";
import { useChatIntentSuggestionsFlag } from "../hooks/useChatIntentSuggestionsFlag";
import { useChatToolTraceFlag } from "../hooks/useChatToolTraceFlag";
import ToolConfirmationCard from "./ToolConfirmationCard";
import { useChatSend } from "../hooks/useChatSend";
import { useChatTurn } from "../hooks/useChatTurn";
import { useCrmChatSession } from "../hooks/useCrmChatSession";
import { useCallRecordings } from "../hooks/useCallRecordings";
import { useManagingPrompts } from "../hooks/useManagingPrompts";
import { promptRequiresCallRecording } from "../constants/chatConstants";
import { useCrmChatPanelLayoutReporter } from "../CrmChatLayoutContext";
import type {
  CrmChatDrawerProps,
  ManagingPromptCatalogEntry,
} from "../types/chat.types";

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 360;
const MAX_WIDTH = 720;
const MOBILE_BREAKPOINT = 640;
const WIDTH_STORAGE_KEY = "crm-chat-drawer:width";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const CrmChatDrawer: React.FC<CrmChatDrawerProps> = ({
  isOpen,
  onClose,
  workspaceId,
  initialRecord,
  initialPromptType,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const reportPanelLayout = useCrmChatPanelLayoutReporter();

  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_WIDTH;
    const stored = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored >= MIN_WIDTH ? stored : DEFAULT_WIDTH;
  });

  const record = initialRecord ?? null;
  const agentLoopEnabled = useChatAgentLoopFlag();
  const toolTraceEnabled = useChatToolTraceFlag();
  const intentSuggestionsEnabled = useChatIntentSuggestionsFlag();
  const {
    sessionId,
    initialMessages,
    initialPromptType: storedPromptType,
    initialPromptName,
    persist,
    clear,
  } = useCrmChatSession({
    workspaceId,
    record,
  });

  const [activePrompt, setActivePrompt] = useState<ManagingPromptCatalogEntry | null>(() => null);
  const [composerText, setComposerText] = useState("");
  const [selectedCallRecordingId, setSelectedCallRecordingId] = useState<string | null>(null);

  const promptsQuery = useManagingPrompts(workspaceId, {
    recordId: record?.recordId ?? null,
    activityId: selectedCallRecordingId,
  });
  const promptCatalog: ManagingPromptCatalogEntry[] = useMemo(
    () => promptsQuery.data ?? [],
    [promptsQuery.data]
  );

  const callRecordingsQuery = useCallRecordings(workspaceId, record?.recordId);
  const callRecordings = callRecordingsQuery.data ?? [];
  const hasCallRecordingWithTranscript = callRecordings.some((r) => r.hasTranscript);
  const activePromptNeedsTranscript =
    activePrompt != null && promptRequiresCallRecording(activePrompt);
  const catalogExecuteReady =
    !activePromptNeedsTranscript || Boolean(selectedCallRecordingId);

  useEffect(() => {
    setSelectedCallRecordingId(null);
  }, [record?.recordId, activePrompt?.promptType]);

  useEffect(() => {
    if (!activePrompt || !promptRequiresCallRecording(activePrompt)) {
      return;
    }
    const withTranscript = callRecordings.filter((r) => r.hasTranscript);
    if (withTranscript.length === 0) {
      return;
    }
    setSelectedCallRecordingId((prev) => {
      if (prev && withTranscript.some((r) => r.activityId === prev)) {
        return prev;
      }
      return withTranscript[0]?.activityId ?? null;
    });
  }, [activePrompt, callRecordings]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialPromptType) {
      const match = promptCatalog.find((p) => p.promptType === initialPromptType);
      if (match) setActivePrompt(match);
    } else if (storedPromptType) {
      const match = promptCatalog.find((p) => p.promptType === storedPromptType);
      if (match) setActivePrompt(match);
    }
  }, [initialPromptType, storedPromptType, promptCatalog, isOpen]);

  const handleMessagesChange = useCallback(
    (messages: Parameters<typeof persist>[0]["messages"]) => {
      persist({
        messages,
        promptType: activePrompt?.promptType,
        promptName: activePrompt?.name,
      });
    },
    [persist, activePrompt?.promptType, activePrompt?.name]
  );

  const chatSend = useChatSend({
    active: !agentLoopEnabled,
    workspaceId,
    record,
    callRecordingActivityId: selectedCallRecordingId,
    initialMessages,
    onMessagesChange: handleMessagesChange,
  });
  const chatTurn = useChatTurn({
    active: agentLoopEnabled,
    workspaceId,
    sessionId,
    record,
    callRecordingActivityId: selectedCallRecordingId,
    initialMessages,
    onMessagesChange: handleMessagesChange,
  });
  const messages = agentLoopEnabled ? chatTurn.messages : chatSend.messages;
  const isSending = agentLoopEnabled ? chatTurn.isStreaming : chatSend.isSending;
  const error = agentLoopEnabled ? chatTurn.error : chatSend.error;
  const regenerate = agentLoopEnabled ? chatTurn.regenerate : chatSend.regenerate;
  const resetChat = agentLoopEnabled ? chatTurn.reset : chatSend.reset;

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleSend = useCallback(async () => {
    const trimmed = composerText.trim();
    if (trimmed === "/") return;
    const promptToUse = activePrompt;
    if (!trimmed && (!promptToUse || promptToUse.promptType === CHAT_PROMPT_CUSTOM_TYPE)) {
      return;
    }
    setComposerText("");
    if (agentLoopEnabled) {
      await chatTurn.sendTurn({ content: trimmed });
      return;
    }
    await chatSend.send({
      content: trimmed || undefined,
      prompt: promptToUse ?? null,
      callRecordingActivityId: selectedCallRecordingId,
    });
  }, [activePrompt, agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]);

  const handleAskDirectly = useCallback(async () => {
    const trimmed = composerText.trim();
    if (!trimmed) return;
    setActivePrompt(null);
    setComposerText("");
    if (agentLoopEnabled) {
      await chatTurn.sendTurn({ content: trimmed });
      return;
    }
    await chatSend.send({
      content: trimmed,
      prompt: null,
      callRecordingActivityId: selectedCallRecordingId,
    });
  }, [agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]);

  const handleSelectPromptForComposer = useCallback((prompt: ManagingPromptCatalogEntry) => {
    setActivePrompt(prompt);
    setComposerText("");
  }, []);

  const handleSelectPrompt = useCallback(
    async (prompt: ManagingPromptCatalogEntry) => {
      if (prompt.promptType === CHAT_PROMPT_CUSTOM_TYPE) {
        setActivePrompt(prompt);
        return;
      }
      setActivePrompt(prompt);
      if (promptRequiresCallRecording(prompt)) {
        return;
      }
      const extra = composerText.trim() || undefined;
      if (agentLoopEnabled) {
        await chatTurn.runCatalogPrompt({
          prompt,
          content: extra,
          callRecordingActivityId: selectedCallRecordingId,
        });
      } else {
        await chatSend.send({
          prompt,
          content: extra,
          callRecordingActivityId: selectedCallRecordingId,
        });
      }
      setComposerText("");
    },
    [agentLoopEnabled, chatSend, chatTurn, composerText, selectedCallRecordingId]
  );

  const handleNewChat = useCallback(() => {
    resetChat();
    clear();
    setActivePrompt(null);
    setComposerText("");
  }, [resetChat, clear]);

  const clampPanelWidth = useCallback(
    (deltaWidth: number) =>
      Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width + deltaWidth)),
    [width]
  );

  const handleResize = useCallback(
    (_e: unknown, _dir: unknown, _ref: HTMLElement, delta: { width: number }) => {
      if (!isOpen || isMobile) return;
      reportPanelLayout({
        isOpen: true,
        width: clampPanelWidth(delta.width),
        isMobile: false,
      });
    },
    [clampPanelWidth, isMobile, isOpen, reportPanelLayout]
  );

  const handleResizeStop = useCallback(
    (_e: unknown, _dir: unknown, _ref: HTMLElement, delta: { width: number }) => {
      const next = clampPanelWidth(delta.width);
      setWidth(next);
      try {
        window.localStorage.setItem(WIDTH_STORAGE_KEY, String(next));
      } catch {
        // ignore storage failures
      }
    },
    [clampPanelWidth]
  );

  const drawerWidth = isMobile ? window.innerWidth : width;

  const headerTitle = useMemo(() => t("Ask"), [t]);

  useEffect(() => {
    reportPanelLayout({
      isOpen,
      width: drawerWidth,
      isMobile,
    });
  }, [isOpen, drawerWidth, isMobile, reportPanelLayout]);

  useEffect(
    () => () => {
      reportPanelLayout({ isOpen: false, width: 0, isMobile: false });
    },
    [reportPanelLayout]
  );

  if (!isOpen) return null;

  const content = (
    <div className="relative flex h-full w-full flex-col overflow-visible border-l border-gray-200 bg-white shadow-[rgb(0_0_0/22%)_0px_25.6px_57.6px_0px,rgb(0_0_0/18%)_0px_4.8px_14.4px_0px]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label={t("Close chat")}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <span>{headerTitle}</span>
          <BetaBadge />
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
        >
          <Plus className="h-3.5 w-3.5" />
          {t("New")}
        </button>
      </div>

      <ChatContextBar
        record={record}
        promptType={activePrompt?.promptType ?? null}
        promptName={activePrompt?.name ?? initialPromptName ?? null}
        onClearPrompt={() => setActivePrompt(null)}
        disabled={isSending}
        callRecordings={callRecordings}
        selectedCallRecordingId={selectedCallRecordingId}
        onSelectCallRecording={setSelectedCallRecordingId}
        callRecordingsLoading={callRecordingsQuery.isLoading}
        requiresCallRecordingWithTranscript={activePromptNeedsTranscript}
      />

      <ChatThread
        messages={messages}
        record={record}
        isLoading={isSending && !agentLoopEnabled}
        isStreaming={agentLoopEnabled && chatTurn.isStreaming}
        streamingMessageId={chatTurn.streamingMessageId ?? undefined}
        showToolTrace={toolTraceEnabled}
        onRegenerate={regenerate}
        promptCatalog={promptCatalog}
        onSelectSuggestedPrompt={handleSelectPrompt}
        emptyState={
          <ChatEmptyState record={record}>
            <PromptGallery
              prompts={promptCatalog}
              recordType={record?.recordType ?? null}
              isLoading={promptsQuery.isLoading}
              onSelectPrompt={handleSelectPrompt}
              hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
              onSelectCustom={() => {
                setActivePrompt({
                  promptType: CHAT_PROMPT_CUSTOM_TYPE,
                  name: t("Custom") as string,
                  description: "",
                  categories: [],
                  requiresRecord: false,
                });
              }}
            />
          </ChatEmptyState>
        }
      />

      {error && messages[messages.length - 1]?.role !== "system" ? (
        <div className="mx-4 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {agentLoopEnabled && chatTurn.pendingToolCall ? (
        <ToolConfirmationCard
          toolCall={chatTurn.pendingToolCall}
          turnId={chatTurn.activeTurnId}
          isSubmitting={chatTurn.confirmSubmitting}
          errorMessage={chatTurn.confirmError}
          onApprove={() => chatTurn.approvePendingTool()}
          onReject={() => chatTurn.rejectPendingTool()}
        />
      ) : null}

      <div className="relative z-20 shrink-0 px-3 pb-3">
        <ChatComposer
        value={composerText}
        onChange={setComposerText}
        onSend={handleSend}
        onStop={
          agentLoopEnabled && chatTurn.activeTurnId ? () => chatTurn.abortTurn() : undefined
        }
        disabled={Boolean(agentLoopEnabled && chatTurn.pendingToolCall)}
        isLoading={isSending}
        isStreaming={agentLoopEnabled && chatTurn.isStreaming}
        activePromptName={activePrompt?.name ?? null}
        onClearPrompt={() => setActivePrompt(null)}
        slashPrompts={promptCatalog}
        recordType={record?.recordType ?? null}
        onSelectPromptForComposer={handleSelectPromptForComposer}
        hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
        catalogExecuteReady={catalogExecuteReady}
        intentSuggestionsEnabled={intentSuggestionsEnabled}
        intentSuggestionPrompts={promptCatalog}
        showIntentSuggestionsWhenEmpty={messages.length === 0}
        onAskDirectly={handleAskDirectly}
        promptPickerSlot={
          <PromptPickerPopover
            prompts={promptCatalog}
            recordType={record?.recordType ?? null}
            onSelect={handleSelectPrompt}
            hasCallRecordingWithTranscript={hasCallRecordingWithTranscript}
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                aria-label={t("Choose a prompt") as string}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("Prompts")}
                <ChevronDown className="h-3 w-3" />
              </button>
            }
          />
        }
      />
      </div>
    </div>
  );

  const panelBody = isMobile ? (
    <div className={`relative h-full w-full ${className ?? ""}`}>{content}</div>
  ) : (
    <Resizable
      size={{ width: drawerWidth, height: "100%" }}
      minWidth={MIN_WIDTH}
      maxWidth={MAX_WIDTH}
      enable={{ left: true }}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      handleClasses={{ left: "crm-chat-drawer-handle" }}
      className={`relative h-full ${className ?? ""}`}
    >
      {content}
    </Resizable>
  );

  // Desktop: docked side panel (not a modal) so record detail stays visible alongside Ask.
  if (!isMobile) {
    return (
      <aside
        role="complementary"
        aria-label={headerTitle as string}
        className="pointer-events-auto fixed right-0 top-0 z-40 flex h-full overflow-visible"
      >
        {panelBody}
      </aside>
    );
  }

  // Mobile: full-screen overlay with scrim (narrow viewports only).
  return (
    <div className="fixed inset-0 z-40 flex overflow-visible">
      <div className="flex-1 bg-black/30" onClick={onClose} aria-hidden="true" />
      <aside role="complementary" aria-label={headerTitle as string} className="relative h-full w-full">
        {panelBody}
      </aside>
    </div>
  );
};

export default CrmChatDrawer;
